import { useEffect, useMemo, useRef, useState } from "react";
import { FiAlertCircle, FiBarChart2, FiCheck, FiChevronDown, FiChevronRight, FiCreditCard, FiPlus } from "react-icons/fi";
import { Trans, useTranslation } from "react-i18next";
import { AppButton } from "@/components/ui/primitives";
import type { InstallmentPurchase } from "../types";
import { InstallmentCard } from "../components/InstallmentCard";
import { InstallmentsDetailModal } from "../components/InstallmentsDetailModal";
import { InstallmentsDebtFormModal } from "../components/InstallmentsDebtFormModal";
import {
  useCreateInstallment,
  useInstallmentsBalance,
  useDeleteInstallment,
  useUpdateInstallment,
} from "@/hooks/useInstallments";
import { useAccountColors, useAccounts } from "@/hooks/useAccounts";
import {
  addMonths,
  assignInstallmentColors,
  filterAccountsWithInstallments,
  formatInterestRate,
  formatShortDate,
  getInstallmentAccountIds,
  getInstallmentDebtSummary,
  keepValidInstallmentAccountIds,
} from "../utils/installmentsFormat";
import { formatCurrency } from "@/utils/formatters";
import { confirmAction, showAppMessage } from "@/components/ui/confirmDialog";
import { PageSkeleton } from "@/components/ui/PageSkeleton";
import { DomainSidebar, DomainSidebarHeader, DomainTwoColumnContent, DomainTwoColumnLayout } from "@/components/ui/DomainSidebar";
import { SearchInput } from "@/components/ui/SearchInput";
import { useNavigationState } from "@/hooks/useNavigationState";
import "../installments.css";

const parseDate = (value: string): number => new Date(`${value}T00:00:00`).getTime();

type InstallmentSort = "next-asc" | "next-desc" | "name-asc" | "name-desc";

const DEBT_BAR_CLASS = ["bg-[var(--ds-card-lilac)]", "bg-[var(--ds-card-mist)]", "bg-[var(--ds-card-sun)]", "bg-[var(--ds-card-lime)]"];


export const InstallmentsView = () => {
  const { t } = useTranslation(["installments", "common"]);
  const [selectedPurchaseId, setSelectedPurchaseId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useNavigationState("installments.searchTerm", "");
  const [selectedAccountIds, setSelectedAccountIds] = useNavigationState<string[]>(
    "installments.accountIds",
    [],
  );
  const [isAccountFilterOpen, setIsAccountFilterOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingInstallmentId, setEditingInstallmentId] = useState<number | null>(null);
  const [sortBy, setSortBy] = useNavigationState<InstallmentSort>("installments.sort", "next-asc");
  const accountFilterRef = useRef<HTMLDivElement>(null);
  const { data: installmentsBalanceData, isLoading, error } = useInstallmentsBalance();
  const updateInstallmentMutation = useUpdateInstallment();
  const deleteInstallmentMutation = useDeleteInstallment();
  const createInstallmentMutation = useCreateInstallment();
  const { data: accounts = [] } = useAccounts();
  const { data: palette = [] } = useAccountColors();

  const accountInfoById = useMemo(() => {
    const map = new Map<number, { name: string }>();
    accounts.forEach((account) => {
      map.set(account.id, { name: account.name });
    });
    return map;
  }, [accounts]);

  const installmentAccountIds = useMemo(
    () => getInstallmentAccountIds(installmentsBalanceData?.installments_balance ?? []),
    [installmentsBalanceData],
  );

  const sortedAccounts = useMemo(
    () => filterAccountsWithInstallments(accounts, installmentAccountIds)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [accounts, installmentAccountIds],
  );

  useEffect(() => {
    if (!installmentsBalanceData) return;

    setSelectedAccountIds((current) => {
      const validAccountIds = keepValidInstallmentAccountIds(current, installmentAccountIds);
      return validAccountIds.length === current.length ? current : validAccountIds;
    });
  }, [installmentsBalanceData, installmentAccountIds, setSelectedAccountIds]);

  useEffect(() => {
    if (!isAccountFilterOpen) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!accountFilterRef.current?.contains(event.target as Node)) setIsAccountFilterOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setIsAccountFilterOpen(false);
      accountFilterRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isAccountFilterOpen]);

  // Multi-select: the pill names the account while there is only one, then falls back to a count.
  const selectedAccountsLabel = selectedAccountIds.length === 0
    ? t("installments.allAccounts")
    : selectedAccountIds.length === 1
      ? sortedAccounts.find((account) => String(account.id) === selectedAccountIds[0])?.name
        ?? t("installments.accountsSelectedCount", { count: 1 })
      : t("installments.accountsSelectedCount", { count: selectedAccountIds.length });

  // The panel stays open while toggling: closing on every pick would make picking a second
  // account cost a reopen.
  const toggleAccount = (accountId: string) => {
    setSelectedAccountIds((current) =>
      current.includes(accountId)
        ? current.filter((id) => id !== accountId)
        : [...current, accountId],
    );
  };

  const clearAccounts = () => {
    setSelectedAccountIds([]);
  };

  const purchases = useMemo<InstallmentPurchase[]>(() => {
    const installments = installmentsBalanceData?.installments_balance ?? [];

    return installments.map((installment) => {
      const accountInfo = accountInfoById.get(installment.account_id);
      const months = installment.num_payments ?? 0;
      const isCompleted = Boolean(installment.completed) || !installment.active;
      const reportedPayments = installment.done_payments_sum ?? installment.done_payments;
      const monthsPaid = isCompleted ? months : reportedPayments;
      const remainingBalance = isCompleted ? 0 : installment.amount_left;
      const schedule = Array.from({ length: months }, (_, index) => {
        const payment = installment.payments[index];
        const dueDate = installment.start_date
          ? addMonths(installment.start_date, index)
          : (payment?.transaction_date ?? "");
        return {
          id: payment?.id ? String(payment.id) : `${installment.id}-p${index + 1}`,
          dueDate,
          amount: payment?.amount ?? installment.monthly_expected ?? 0,
          status: index < monthsPaid ? "paid" : "pending",
        } as const;
      });

      const nextChargeDate =
        !isCompleted && installment.start_date && monthsPaid < months
          ? addMonths(installment.start_date, monthsPaid)
          : null;

      const payoffDate = installment.payoff_date
        ?? (installment.start_date && months > 0 ? addMonths(installment.start_date, months - 1) : installment.start_date)
        ?? "";

      return {
        id: String(installment.id),
        name: installment.name,
        merchant: installment.description ?? t("installments.tableHeaderInstallment"),
        accountId: String(installment.account_id),
        accountName: accountInfo?.name ?? `Account ${installment.account_id}`,
        initialAmount: installment.initial_amount ?? undefined,
        totalAmount: installment.amount,
        paidAmount: installment.done_amount,
        months,
        monthlyPayment: installment.monthly_expected ?? 0,
        interestRate: installment.interest_rate,
        totalInterest: installment.total_interest,
        remainingInterest: installment.remaining_interest,
        monthsPaid,
        remainingBalance,
        startDate: installment.start_date ?? "",
        nextChargeDate,
        payoffDate,
        status: isCompleted ? "completed" : "active",
        schedule,
      };
    });
  }, [installmentsBalanceData, accountInfoById, t]);
  const filteredPurchases = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase();

    return purchases.filter((purchase) => {
      if (selectedAccountIds.length > 0 && !selectedAccountIds.includes(purchase.accountId)) return false;
      if (!query) return true;

      return [purchase.name, purchase.merchant, purchase.accountName].some((value) =>
        value.toLocaleLowerCase().includes(query),
      );
    });
  }, [purchases, searchTerm, selectedAccountIds]);

  const activePurchases = useMemo(
    () =>
      filteredPurchases
        .filter((purchase) => purchase.status === "active")
        .sort((a, b) => {
          if (sortBy === "name-asc") return a.name.localeCompare(b.name);
          if (sortBy === "name-desc") return b.name.localeCompare(a.name);
          if (!a.nextChargeDate && !b.nextChargeDate) return 0;
          if (!a.nextChargeDate) return 1;
          if (!b.nextChargeDate) return -1;
          const diff = parseDate(a.nextChargeDate) - parseDate(b.nextChargeDate);
          return sortBy === "next-desc" ? -diff : diff;
        }),
    [filteredPurchases, sortBy],
  );

  const completedPurchases = useMemo(
    () =>
      filteredPurchases
        .filter((purchase) => purchase.status === "completed")
        .sort((a, b) => parseDate(b.payoffDate) - parseDate(a.payoffDate)),
    [filteredPurchases],
  );

  // Every card on screen (active and completed) gets a distinct palette color.
  const installmentColors = useMemo(
    () => assignInstallmentColors(
      [...activePurchases, ...completedPurchases].map((purchase) => purchase.id),
      palette.map((color) => color.hex),
    ),
    [activePurchases, completedPurchases, palette],
  );

  const { upcomingTotal, outstandingTotal, accountDebtSummary } = useMemo(
    () => getInstallmentDebtSummary(activePurchases),
    [activePurchases],
  );

  const hasUnknownInterest = activePurchases.some((purchase) => purchase.interestRate == null);

  const handleDelete = async (purchase: InstallmentPurchase) => {
    const id = Number(purchase.id);
    const confirmed = await confirmAction({
      title: t("common.prompts.deleteInstallment"),
      body: purchase.name,
      confirmLabel: t("common.actions.delete", { defaultValue: "Delete" }),
      cancelLabel: t("common.actions.cancel", { defaultValue: "Cancel" }),
      tone: "danger",
    });
    if (!confirmed) return;
    await deleteInstallmentMutation.mutateAsync(id);
  };

  const handleOpenEdit = (purchase: InstallmentPurchase) => {
    setEditingInstallmentId(Number(purchase.id));
    setIsFormOpen(true);
  };

  const openPurchaseDetail = (purchase: InstallmentPurchase) => {
    setSelectedPurchaseId(purchase.id);
  };

  const selectedPurchase = purchases.find((purchase) => purchase.id === selectedPurchaseId) ?? null;

  const getProgressLabel = (purchase: InstallmentPurchase) => {
    if (purchase.months <= 0) return t("installments.typeFixed");

    const paid = Math.min(Math.max(0, purchase.monthsPaid), purchase.months);
    const parts = [`${paid}/${purchase.months} ${t("installments.payments")}`];

    if (purchase.remainingBalance > 0) {
      parts.push(t("installments.remainingShort", { amount: formatCurrency(purchase.remainingBalance) }));
    }

    parts.push(t("installments.totalShort", { amount: formatCurrency(purchase.totalAmount) }));
    return parts.join(" · ");
  };

  const getProgressPercentage = (purchase: InstallmentPurchase) => {
    if (purchase.status === "completed") return 100;

    if (purchase.months > 0) {
      return Math.min(100, Math.max(0, (purchase.monthsPaid / purchase.months) * 100));
    }

    const total = purchase.initialAmount ?? purchase.totalAmount;
    const paid = purchase.paidAmount ?? Math.max(0, total - purchase.remainingBalance);
    return total > 0 ? Math.min(100, Math.max(0, (paid / total) * 100)) : 0;
  };

  const renderPurchaseCard = (purchase: InstallmentPurchase) => {
    const progressPercentage = getProgressPercentage(purchase);
    const percent = Math.round(progressPercentage);

    return (
      <InstallmentCard
        key={purchase.id}
        name={purchase.name}
        accountLabel={`${t("common:labels.account")}: ${purchase.accountName}`}
        color={installmentColors.get(purchase.id)}
        amount={formatCurrency(purchase.monthlyPayment)}
        amountSuffix={t("installments.perMonthSuffix")}
        nextChargeLabel={
          purchase.status === "completed"
            ? undefined
            : purchase.nextChargeDate
              ? t("installments.chargeDate", { date: formatShortDate(purchase.nextChargeDate) })
              : t("installments.chargeDatePending")
        }
        summary={getProgressLabel(purchase)}
        rateLabel={purchase.interestRate == null ? null : t("installments.interestRateKnown", { rate: formatInterestRate(purchase.interestRate) })}
        missingRateLabel={t("installments.interestRateMissing")}
        percent={progressPercentage}
        percentLabel={`${percent}%`}
        progressLabel={`${t("installments.progress")}: ${purchase.name}`}
        progressValueText={t("installments.paidProgress", { percent })}
        menuLabel={t("installments.cardMenuLabel", { name: purchase.name })}
        editLabel={t("common.actions.edit")}
        deleteLabel={t("common.actions.delete")}
        onOpen={() => openPurchaseDetail(purchase)}
        onEdit={() => handleOpenEdit(purchase)}
        onDelete={async () => {
          try {
            await handleDelete(purchase);
          } catch {
            showAppMessage({ title: t("common.errors.deleteInstallment"), tone: "danger" });
          }
        }}
      />
    );
  };

  if (isLoading) {
    return <PageSkeleton lines={4} />;
  }

  if (error) {
    return (
      <main className="app-page flex items-center justify-center">
        <div className="app-surface border-[color:color-mix(in_srgb,var(--app-danger)_22%,var(--app-border)_78%)] bg-[color:color-mix(in_srgb,var(--app-danger)_8%,var(--app-surface)_92%)] px-8 py-6 text-center">
          <p className="text-lg font-medium text-[var(--app-text)]">{t("installments.loadError")}</p>
        </div>
      </main>
    );
  }

  const sortOptions: { value: InstallmentSort; label: string }[] = [
    { value: "next-asc", label: t("installments.sortNextAsc") },
    { value: "next-desc", label: t("installments.sortNextDesc") },
    { value: "name-asc", label: t("installments.sortNameAsc") },
    { value: "name-desc", label: t("installments.sortNameDesc") },
  ];

  return (
    <main className="app-page installments-page">
      <div className="app-container max-w-none !overflow-visible">
        <div className="relative mx-auto w-full max-w-2xl min-w-0 md:max-w-none">
          <DomainTwoColumnLayout className="!h-auto !overflow-visible">
            <DomainSidebar className="lg:sticky lg:top-32 lg:h-fit lg:overflow-visible">
              <DomainSidebarHeader greeting={t("common:labels.greeting")} title={t("common:nav.installments")} />

              <section className="app-domain-sidebar-section">
                <p className="installments-hero-label">{t("installments.heroUpcomingPayments")}</p>
                <p className="app-domain-sidebar-value">{formatCurrency(upcomingTotal)}</p>
                <p className="installments-hero-outstanding">
                  <Trans t={t} i18nKey="installments.heroOutstandingBold" values={{ amount: formatCurrency(outstandingTotal) }} components={{ strong: <strong /> }} />
                </p>
              </section>

              {accountDebtSummary.length > 0 ? (
                <section className="app-domain-sidebar-section" aria-label={t("installments.debtByAccountTitle")}>
                  <p className="installments-debt-overline">{t("installments.debtByAccountLabel")}</p>
                  <h2 className="installments-debt-title">{t("installments.debtByAccountTitle")}</h2>
                  <div className="flex flex-col gap-4">
                    {accountDebtSummary.map((account, index) => {
                      const width = outstandingTotal > 0 ? (account.amount / outstandingTotal) * 100 : 0;

                      return (
                        <div key={account.accountName}>
                          <div className="installments-debt-row">
                            <span className="min-w-0">
                              <span className="block truncate">{account.accountName}</span>
                              <small>{t("installments.installmentsCount", { count: account.count })}</small>
                            </span>
                            <b className="shrink-0 font-medium tabular-nums">{formatCurrency(account.amount)}</b>
                          </div>
                          <div
                            className="mt-2 flex h-4"
                            role="img"
                            aria-label={`${account.accountName}: ${Math.round(width)}%`}
                          >
                            <i
                              className={`block rounded-full ${DEBT_BAR_CLASS[index % DEBT_BAR_CLASS.length]}`}
                              style={{ width: `${Math.min(100, Math.max(0, width))}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ) : null}
            </DomainSidebar>

            <DomainTwoColumnContent className="!h-auto !overflow-visible pb-20">
              {purchases.length === 0 ? (
                <section className="pb-6">
                  <div className="app-panel-raised px-5 py-6">
                    <p className="text-base text-[var(--app-text-secondary)]">{t("installments.emptyStateBody")}</p>
                    <AppButton
                      type="button"
                      tone="primary"
                      shape="pill"
                      className="mt-5"
                      onClick={() => {
                        setEditingInstallmentId(null);
                        setIsFormOpen(true);
                      }}
                      leadingIcon={<FiPlus className="h-4 w-4" />}
                    >
                      {t("installments.newInstallment")}
                    </AppButton>
                  </div>
                </section>
              ) : (
                <section className="pb-8">
                  <div
                    className="sticky top-2 z-10 mb-4 flex flex-col gap-3 py-2 lg:top-24 xl:flex-row xl:items-center"
                    role="toolbar"
                    aria-label={t("installments.filterToolbarLabel")}
                  >
                    <SearchInput
                      className="min-w-0 flex-1"
                      value={searchTerm}
                      onChange={setSearchTerm}
                      placeholder={t("installments.searchPlaceholder")}
                      label={t("installments.searchPlaceholder")}
                    />
                    <div ref={accountFilterRef} className="relative">
                      <button
                        type="button"
                        onClick={() => setIsAccountFilterOpen((current) => !current)}
                        aria-haspopup="true"
                        aria-expanded={isAccountFilterOpen}
                        aria-label={t("installments.accountFilterValue", { value: selectedAccountsLabel })}
                        className="installments-pill app-focus-ring"
                      >
                        <FiCreditCard className="h-[18px] w-[18px]" aria-hidden="true" />
                        {t("installments.accountFilterValue", { value: selectedAccountIds.length === 0 ? t("installments.allAccountsShort") : selectedAccountsLabel })}
                        <FiChevronDown className={`h-4 w-4 transition-transform ${isAccountFilterOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                      </button>
                      {isAccountFilterOpen ? (
                        <div className="absolute right-0 top-[calc(100%+8px)] z-30 max-h-72 min-w-64 overflow-y-auto overscroll-contain rounded-[var(--ds-radius-md)] border border-[var(--ds-line)] bg-[var(--ds-surface-raised)] p-2 text-[var(--ds-ink)] shadow-[var(--ds-shadow-2)]">
                          <button
                            type="button"
                            onClick={clearAccounts}
                            aria-pressed={selectedAccountIds.length === 0}
                            className="app-focus-ring flex min-h-11 w-full items-center gap-2 rounded-[var(--ds-radius-sm)] px-3 text-left text-[15px] hover:bg-[var(--ds-ground)]"
                          >
                            <span className={`min-w-0 flex-1 truncate ${selectedAccountIds.length === 0 ? "font-medium" : ""}`}>{t("installments.allAccounts")}</span>
                            {selectedAccountIds.length === 0 ? <FiCheck className="h-4 w-4 flex-none" aria-hidden="true" /> : null}
                          </button>
                          {sortedAccounts.map((account) => {
                            const selected = selectedAccountIds.includes(String(account.id));

                            return (
                              <button
                                type="button"
                                key={account.id}
                                onClick={() => toggleAccount(String(account.id))}
                                aria-pressed={selected}
                                className="app-focus-ring flex min-h-11 w-full items-center gap-2 rounded-[var(--ds-radius-sm)] px-3 text-left text-[15px] hover:bg-[var(--ds-ground)]"
                              >
                                <span className={`min-w-0 flex-1 truncate ${selected ? "font-medium" : ""}`}>{account.name}</span>
                                {selected ? <FiCheck className="h-4 w-4 flex-none" aria-hidden="true" /> : null}
                              </button>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                    <label className="installments-pill relative cursor-pointer">
                      <FiBarChart2 className="h-[18px] w-[18px]" aria-hidden="true" />
                      <span>{sortOptions.find((option) => option.value === sortBy)?.label}</span>
                      <select
                        value={sortBy}
                        onChange={(event) => setSortBy(event.target.value as InstallmentSort)}
                        aria-label={t("installments.sortByLabel")}
                        className="app-focus-ring absolute inset-0 h-full w-full cursor-pointer rounded-full opacity-0"
                      >
                        {sortOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                  </div>

                  {hasUnknownInterest ? (
                    <div className="installments-info-note mb-6" role="note">
                      <FiAlertCircle className="h-[22px] w-[22px] shrink-0" aria-hidden="true" />
                      <div>
                        <strong>{t("installments.interestNoteTitle")}</strong>
                        <br />
                        {t("installments.interestNoteBody")}
                      </div>
                    </div>
                  ) : null}

                  <div className="mb-3">
                    <h2 className="m-0 text-xl font-medium leading-6 tracking-[-0.01em] text-[var(--ds-ink)]">{t("installments.activeInstallments")}</h2>
                    <p className="mt-1 text-sm leading-5 text-[var(--ds-ink-muted)]">{t("installments.installmentsCount", { count: activePurchases.length })}</p>
                  </div>

                  {activePurchases.length === 0 ? (
                    <p className="py-6 text-sm text-[var(--ds-ink-muted)]">{t("installments.noActiveInstallments")}</p>
                  ) : (
                    <div className="flex flex-col gap-4">
                      {activePurchases.map((purchase) => renderPurchaseCard(purchase))}
                    </div>
                  )}

                  {completedPurchases.length > 0 ? (
                    <details className="group mt-8">
                      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 px-1 text-sm">
                        <div className="min-w-0">
                          <p className="font-medium text-[var(--ds-ink)]">{t("installments.completedInstallments")}</p>
                          <p className="mt-1 text-[var(--ds-ink-muted)]">{t("installments.itemsCount", { count: completedPurchases.length })}</p>
                        </div>
                        <span className="inline-flex items-center gap-1 text-[var(--ds-ink-muted)]">
                          {t("installments.viewAll")}
                          <FiChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
                        </span>
                      </summary>

                      <div className="mt-3 flex flex-col gap-4">
                        {completedPurchases.map((purchase) => renderPurchaseCard(purchase))}
                      </div>
                    </details>
                  ) : null}
                </section>
              )}
            </DomainTwoColumnContent>
          </DomainTwoColumnLayout>
        </div>
      </div>

      <InstallmentsDetailModal
        purchase={selectedPurchase}
        isOpen={Boolean(selectedPurchase)}
        onClose={() => setSelectedPurchaseId(null)}
      />

      <InstallmentsDebtFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        accounts={accounts}
        initialData={
          editingInstallmentId
            ? (() => {
                const installment = installmentsBalanceData?.installments_balance.find(
                  (item) => item.id === editingInstallmentId,
                );
                if (!installment) return null;
                return {
                  id: installment.id,
                  name: installment.name,
                  account_id: installment.account_id,
                  description: installment.description ?? "",
                  initial_amount: installment.initial_amount ?? installment.amount,
                  amount: installment.amount,
                  interest_rate: installment.interest_rate,
                  start_date: installment.start_date ?? undefined,
                  num_payments: installment.num_payments ?? undefined,
                  done_payments: installment.done_payments,
                  active: installment.active,
                };
              })()
            : null
        }
        onSubmit={(data, id) => {
          if (id) {
            updateInstallmentMutation.mutate({ id, data });
          } else {
            createInstallmentMutation.mutate(data);
          }
          setIsFormOpen(false);
        }}
      />

    </main>
  );
};
